import { errorText } from '../errorText'
/**
 * Решение согласующего по открытой задаче: комментарий (обязателен для отклонения/доработки и
 * для рук./Рук.СП/ГД в подборе), правка документа перед переотправкой с доработки (К1), скан
 * приказа/подписанного договора и сами кнопки решений.
 *
 * Владеет своей формой и мутацией decide; родитель (ProcessActions) только считает, что доступно.
 */
import { useState } from 'react'
import { Platform } from 'react-native'
import { Input, Text, TextArea, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp, type Localized } from '../../../i18n'
import { pickFileWeb, pickFilesWeb } from '../../../lib/files'
import { tone } from '../../../theme/tones'
import { Button, DateField, Icon, IconButton, OptionSelect } from '../../../ui'
import { CONTRACT_ENUM_LABEL } from '../../../lib/edo-labels'
import type { EdoDecision, EdoStage, Profile, RequestCard } from '../../../lib/types'
import { PersonPickField } from '../forms/PersonPickField'
import { SignWithSima } from './SignWithSima'
import { useDecideTask } from '../useEdo'

const DECISION_LABEL: Record<EdoDecision, Localized> = {
  approve: { ru: 'Согласовать', en: 'Approve' },
  reject: { ru: 'Отклонить', en: 'Reject' },
  rework: { ru: 'На доработку', en: 'Rework' },
  sign: { ru: 'Подписать', en: 'Sign' },
  resubmit: { ru: 'Переотправить', en: 'Resubmit' },
  cancel: { ru: 'Отменить', en: 'Cancel' },
}

/** Срок действия договора: даты бывают сколь угодно прошлыми (договор подписан до шага автора). */
const CONTRACT_DATE_MIN = '2000-01-01'

/** Шаг автора договора: те же решения, но смысл — отправка в 1С или отказ контрагента. */
const SIGNED_COPY_LABEL: Partial<Record<EdoDecision, Localized>> = {
  approve: { ru: 'Отправить в 1С', en: 'Send to 1C', az: '1C-yə göndər' },
  cancel: { ru: 'Договор не подписан', en: 'Contract not signed', az: 'Müqavilə imzalanmayıb' },
}

/** Увольнение (ТЗ-2026-008): «Вернуть на доработку» вместо «Отклонить», у CEO — «Утвердить» (ДА). */
function terminationLabel(stage: EdoStage | undefined, d: EdoDecision): Localized | undefined {
  if (d === 'rework') return { ru: 'Вернуть на доработку', en: 'Return for rework', az: 'Düzəlişə qaytar' }
  if (stage === 'CEO' && d === 'approve') return { ru: 'Утвердить', en: 'Approve', az: 'Təsdiq et' }
  if (stage === 'ORDER' && d === 'approve') return { ru: 'Выполнено', en: 'Done', az: 'İcra edildi' }
  return undefined
}

/** Подпись решения на шаге — общая для кнопки в футере карточки и кнопки подтверждения здесь. */
export function decisionLabel(d: EdoDecision, stage: EdoStage | undefined, type: RequestCard['type'] | undefined): Localized {
  // Смысловое действие шага SEARCH — «поиск начат», а не согласование (аудит §5.3);
  // шага автора договора — «отправить в 1С» / «договор не подписан».
  return stage === 'SEARCH' && d === 'approve'
    ? { ru: 'Начать поиск', en: 'Start search' }
    : (stage === 'SIGNED_COPY' && SIGNED_COPY_LABEL[d]) || (type === 'TERMINATION' && terminationLabel(stage, d)) || DECISION_LABEL[d]
}

export function DecisionPanel({
  taskId,
  stage,
  detail,
  decisions,
  needComment,
  needScan,
  contractFinal,
  hrDeputy = false,
  reworkEdit,
  detailPending,
  onDone,
}: {
  taskId?: string
  stage?: EdoStage
  detail?: RequestCard
  /** Решения, разрешённые на стадии (пусто — кнопок нет). */
  decisions: EdoDecision[]
  /** Обяз. комментарий на любом решении (рук./Рук.СП/ГД в подборе, ФТ-04). */
  needComment: boolean
  /** Скан обязателен: приказы шага ORDER (можно несколько) или подписанный договор (один). */
  needScan: boolean
  /** Скан — именно подписанный договор (меняет тексты подсказок). */
  contractFinal: boolean
  /** Шаг HR заявления на отпуск: кадровик может поменять замещающего вместе с «Согласовать». */
  hrDeputy?: boolean
  /** Инициатор правит документ перед «Отправить снова» (К1). */
  reworkEdit: boolean
  /** Живая карточка ещё грузится — решения блокируем. */
  detailPending: boolean
  onDone: () => void
}) {
  const { t } = useApp()
  // Причина обязательна: отказ/доработка и «договор не подписан» на шаге автора.
  const needsReason = (d: EdoDecision) =>
    d === 'reject' || d === 'rework' || (stage === 'SIGNED_COPY' && d === 'cancel')
  const decide = useDecideTask()
  const [comment, setComment] = useState('')
  // Сканы шага: приказов по заявке бывает несколько (28.09.2026), подписанный договор — один файл.
  const [scans, setScans] = useState<File[]>([])
  // Подписанный через SIMA экземпляр вместо скана: в решение уходит его uploadId.
  const [signed, setSigned] = useState<{ uploadId: string; name: string } | null>(null)
  // Срок действия договора собирается здесь, а не в форме подачи: точные даты появляются вместе
  // с подписанным экземпляром — тем самым сканом, что рядом (решение бизнеса 27.08.2026).
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' })
  const setDate = (k: 'dateFrom' | 'dateTo') => (v: string) =>
    setDates((prev) => ({ ...prev, [k]: v }))
  const datesFilled = !!dates.dateFrom && !!dates.dateTo
  const datesOrdered = !datesFilled || dates.dateTo >= dates.dateFrom
  // Замещающий (HR, заявление на отпуск). undefined — HR не трогал, показываем того, что в карточке;
  // null — HR убрал замещающего. Так поле не зависит от момента, когда подгрузится карточка.
  const [pickedDeputy, setPickedDeputy] = useState<Pick<Profile, 'id' | 'fullName' | 'jobTitle' | 'avatarUrl'> | null | undefined>(undefined)
  const currentDeputyId = detail?.deputy?.id ?? null
  const deputy = pickedDeputy === undefined ? detail?.deputy ?? null : pickedDeputy
  const [revise, setRevise] = useState<Record<string, string> | null>(null)
  const [reviseFile, setReviseFile] = useState<File | null>(null)
  // Ошибка загрузки файла (upload идёт ДО мутации; её reject иначе терялся бы — аудит §3.4).
  const [uploadErr, setUploadErr] = useState('')
  // Файлы грузятся ДО мутации, по одному: без флага второй клик за это время грузил бы их заново
  // и отправлял второе решение.
  const [uploading, setUploading] = useState(false)

  const payloadOf = (detail?.payload ?? {}) as Record<string, unknown>
  /**
   * Показываем то, что чаще всего и просят исправить: у договора — предмет, сумму, условия НДС
   * и признак приложения; у произвольного документа — тему и текст. Остальные поля договора
   * реестр на сервере тоже открывает, но полноразмерная форма внутри панели решения — это уже
   * второй экран подачи.
   */
  const reviseFields: {
    key: string
    label: Localized
    multiline?: boolean
    numeric?: boolean
    boolean?: boolean
    maxLength?: number
  }[] =
    detail?.type === 'TERMINATION'
      ? [
          // Увольнение: дата (не раньше месяца от первой подачи — проверит сервер) и причина.
          { key: 'terminationDate', label: { ru: 'Дата увольнения (ГГГГ-ММ-ДД)', en: 'Termination date (YYYY-MM-DD)', az: 'İşdən çıxma tarixi (İİİİ-AA-GG)' }, maxLength: 10 },
          { key: 'reason', label: { ru: 'Причина увольнения', en: 'Reason', az: 'İşdən çıxma səbəbi' }, multiline: true },
        ]
      : detail?.type === 'CONTRACT'
      ? [
          { key: 'subject', label: { ru: 'Предмет договора', en: 'Subject' }, multiline: true },
          { key: 'amount', label: { ru: 'Сумма', en: 'Amount' }, numeric: true },
          { key: 'vatTerms', label: { ru: 'Условия НДС', en: 'VAT terms', az: 'ƏDV şərtləri' }, maxLength: 300 },
          {
            key: 'hasAdditionalAppendix',
            label: { ru: 'Приложение к договору', en: 'Contract appendix', az: 'Müqaviləyə əlavə' },
            boolean: true,
          },
        ]
      : [
          { key: 'title', label: { ru: 'Тема', en: 'Subject' } },
          { key: 'body', label: { ru: 'Текст документа', en: 'Document text' }, multiline: true },
        ]
  // Значение поля: правленое, иначе исходное из payload. null в revise = «не трогали».
  const reviseValue = (key: string) => revise?.[key] ?? String(payloadOf[key] ?? '')

  /** Решение по задаче; на шаге приказа при «Выполнено» сперва грузим сканы и передаём их uploadId.
   *  При «Отклонить» (Q3: приказ не подписан) скан не нужен — уходит только причина в comment. */
  const doDecide = async (d: EdoDecision) => {
    if (!taskId || decide.isPending || uploading) return
    setUploadErr('')
    // Отправка договора в 1С закрывается только вместе со сроком действия — бэк без него вернёт 400.
    // При «Договор не подписан» (cancel) срок не нужен: договора не будет.
    if (contractFinal && d === 'approve' && (!datesFilled || !datesOrdered)) return
    const scanUploadIds: string[] = []
    if (needScan && d === 'approve' && signed) {
      // Файл уже лежит в хранилище — подписал его SIMA, грузить нечего.
      scanUploadIds.push(signed.uploadId)
    } else if (needScan && d === 'approve') {
      if (!scans.length) return
      setUploading(true)
      try {
        for (const f of scans) {
          const form = new FormData()
          form.append('file', f as unknown as Blob)
          scanUploadIds.push((await api.uploadEdoDoc(form)).id)
        }
      } catch (e) {
        // Неверный тип / >25 МБ / сеть: без catch reject из async-onPress уходил в никуда,
        // пользователь видел «ничего не произошло».
        setUploadErr(t(errorText(e)))
        return
      } finally {
        setUploading(false)
      }
    }
    // К1: вместе с переотправкой уходят исправленные поля и новая редакция. Шлём ТОЛЬКО
    // изменённое — патч из неизменённых значений заставил бы бэк писать «исправлено: subject»
    // там, где ничего не исправляли.
    let reviseArg: { payload?: Record<string, unknown>; uploadIds?: string[] } | undefined
    if (reworkEdit && d === 'resubmit') {
      const patch: Record<string, unknown> = {}
      for (const f of reviseFields) {
        const next = revise?.[f.key]
        if (next === undefined) continue
        const was = String(payloadOf[f.key] ?? '')
        if (next.trim() === was.trim()) continue
        patch[f.key] = f.boolean ? next === 'true' : f.numeric ? Number(next) : next.trim()
      }
      const uploadIds: string[] = []
      if (reviseFile) {
        try {
          const form = new FormData()
          form.append('file', reviseFile as unknown as Blob)
          uploadIds.push((await api.uploadEdoDoc(form)).id)
        } catch (e) {
          setUploadErr(t(errorText(e)))
          return
        }
      }
      if (Object.keys(patch).length || uploadIds.length) {
        reviseArg = { payload: Object.keys(patch).length ? patch : undefined, uploadIds }
      }
    }
    decide.mutate(
      {
        id: taskId,
        decision: d,
        comment: comment.trim() || undefined,
        scanUploadIds: scanUploadIds.length ? scanUploadIds : undefined,
        revise: reviseArg,
        dates: contractFinal && d === 'approve' ? dates : undefined,
        // Бэк принимает deputyId только на HR-шаге отпуска при approve — на отказе и доработке ключа нет;
        // и без изменения его тоже нет (null — осознанный «снять»).
        deputyId: hrDeputy && d === 'approve' && (deputy?.id ?? null) !== currentDeputyId ? deputy?.id ?? null : undefined,
      },
      { onSuccess: onDone },
    )
  }

  return (
    <>
      {/* Комментарий к решению: обязателен для reject/rework (Э5) и для рук./Рук.СП/ГД в подборе (ФТ-04).
          На approve-only шагах (старт поиска, бронь, CFO…) отклонить нельзя — не обещаем этого в плейсхолдере. */}
      {decisions.length > 0 && (
        <TextArea
          value={comment}
          onChangeText={setComment}
          maxLength={1000}
          placeholder={t(needComment
            ? { ru: 'Комментарий (обязателен)', en: 'Comment (required)' }
            : stage === 'SIGNED_COPY'
              ? { ru: 'Комментарий (обязателен, если договор не подписан)', en: 'Comment (required if the contract is not signed)', az: 'Şərh (müqavilə imzalanmayıbsa, məcburidir)' }
              : decisions.some((d) => d === 'reject' || d === 'rework')
                ? { ru: 'Комментарий (обязателен при отклонении/доработке)', en: 'Comment (required to reject/rework)' }
                : { ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}
          placeholderTextColor="$tertiary"
          minHeight={52} fontSize={14} color="$color" backgroundColor="$bgLayout" borderWidth={1}
          borderColor="$borderColor" borderRadius={7} paddingHorizontal={11} paddingVertical={9}
          focusStyle={{ borderColor: '$primary' }}
        />
      )}

      {/* К1: правка документа перед переотправкой с доработки */}
      {reworkEdit && (
        <YStack gap={8}>
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Исправьте документ и отправьте снова — согласование пойдёт заново с первого согласующего. Маршрут и компанию изменить нельзя.',
              en: 'Fix the document and resubmit — approval restarts from the first approver. The route and company cannot be changed.',
            })}
          </Text>
          {reviseFields.map((f) => (
            <YStack key={f.key} gap={4}>
              <Text fontSize={12} fontWeight="600" color="$muted">{t(f.label)}</Text>
              {f.boolean ? (
                <OptionSelect
                  options={[
                    { value: 'true', label: t(CONTRACT_ENUM_LABEL.true) },
                    { value: 'false', label: t(CONTRACT_ENUM_LABEL.false) },
                  ]}
                  value={['true', 'false'].includes(reviseValue(f.key)) ? [reviseValue(f.key)] : []}
                  onChange={(next) => setRevise({ ...(revise ?? {}), [f.key]: next[0] })}
                />
              ) : f.multiline ? (
                <TextArea
                  value={reviseValue(f.key)}
                  onChangeText={(v: string) => setRevise({ ...(revise ?? {}), [f.key]: v })}
                  minHeight={90} fontSize={13} color="$color" backgroundColor="$bgLayout"
                  borderWidth={1} borderColor="$borderColor" borderRadius={7}
                  paddingHorizontal={11} paddingVertical={9}
                  focusStyle={{ borderColor: '$primary' }}
                />
              ) : (
                <Input
                  value={reviseValue(f.key)}
                  onChangeText={(v: string) =>
                    setRevise({ ...(revise ?? {}), [f.key]: f.numeric ? v.replace(/[^\d.]/g, '') : v })
                  }
                  keyboardType={f.numeric ? 'decimal-pad' : 'default'}
                  maxLength={f.maxLength}
                  height={34} fontSize={13} color="$color" backgroundColor="$bgLayout"
                  borderWidth={1} borderColor="$borderColor" borderRadius={6} paddingHorizontal={9}
                  focusStyle={{ borderColor: '$primary' }}
                />
              )}
            </YStack>
          ))}
          {detail?.type === 'TERMINATION' ? null : Platform.OS === 'web' ? (
            <Button
              variant="default" block
              icon={<Icon name="paperclip" size={15} color="$muted" />}
              onPress={async () => setReviseFile(await pickFileWeb('.pdf,.doc,.docx,.jpg,.jpeg,.png'))}
            >
              {reviseFile
                ? `📎 ${reviseFile.name}`
                : t({ ru: 'Приложить новую редакцию', en: 'Attach the new revision' })}
            </Button>
          ) : (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Прикрепление файлов доступно в веб-версии портала', en: 'File upload is available in the web version' })}
            </Text>
          )}
          <Text fontSize={11} color="$muted">
            {t({ ru: 'Прежние вложения остаются в карточке — согласующие видят, что смотрели на первом круге.',
                 en: 'Previous attachments stay on the card — approvers can see what they reviewed in the first round.' })}
          </Text>
        </YStack>
      )}

      {/* Скан: приказ (командировка v1.2) либо подписанный сторонами договор (ТЗ-2026-015) */}
      {needScan && (
        <YStack gap={6}>
          {detail?.orderSla && (
            <Text fontSize={12} fontWeight="600" color={detail.orderSla.overdue ? tone('red').text : '$muted'}>
              {detail.orderSla.overdue
                ? t({ ru: `⏱ SLA просрочен (${detail.orderSla.days} раб. дн.)`, en: `⏱ SLA overdue (${detail.orderSla.days} working days)`, az: `⏱ SLA gecikib (${detail.orderSla.days} iş günü)` })
                : t({ ru: `⏱ SLA: до ${new Date(detail.orderSla.dueAt).toLocaleDateString('ru-RU')} (${detail.orderSla.days} раб. дн.)`, en: `⏱ SLA: by ${new Date(detail.orderSla.dueAt).toLocaleDateString('en-GB')} (${detail.orderSla.days} working days)`, az: `⏱ SLA: ${new Date(detail.orderSla.dueAt).toLocaleDateString('az-AZ')} tarixinədək (${detail.orderSla.days} iş günü)` })}
            </Text>
          )}
          <Text fontSize={12} color="$muted">
            {contractFinal
              ? t({
                  ru: 'Договор согласован. Приложите скан экземпляра, подписанного обеими сторонами, и срок действия — после этого договор уйдёт в 1С. Если контрагент не подписал — «Договор не подписан» с причиной.',
                  en: 'The contract is approved. Attach the scan of the copy signed by both parties and the validity period — then the contract goes to 1C. If the counterparty did not sign, use "Contract not signed" with a reason.',
                  az: 'Müqavilə razılaşdırılıb. Hər iki tərəfin imzaladığı nüsxənin skanını və qüvvədə olma müddətini əlavə edin — bundan sonra müqavilə 1C-yə göndəriləcək. Kontragent imzalamayıbsa — səbəbi ilə «Müqavilə imzalanmayıb».',
                })
              : t({
                  ru: 'Приказ создан в 1С. Распечатайте, подпишите у CEO и приложите скан — без него «Выполнено» недоступно. Если приказ не подписан — «Отклонить» с указанием причины (скан не нужен).',
                  en: 'The order is created in 1C. Print it, get the CEO signature and attach the scan — "Done" is unavailable without it. If the order is not signed, use "Reject" with a reason (no scan needed).',
                })}
          </Text>
          {contractFinal && signed ? (
            <Text fontSize={13} color={tone('green').text}>
              {t({ ru: 'Подписан через SIMA İmza', en: 'Signed with SIMA İmza', az: 'SIMA İmza ilə imzalanıb' })}: {signed.name}
            </Text>
          ) : contractFinal ? (
            /* Подпись через SIMA — вместо скана: подписанный экземпляр появляется сразу в
               хранилище, и его uploadId уходит в решение шага как обычный скан. */
            <SignWithSima attachments={detail?.attachments} onSigned={setSigned} />
          ) : null}
          {Platform.OS === 'web' && contractFinal && !signed ? (
            <Button
              variant="default"
              block
              icon={<Icon name="paperclip" size={15} color="$muted" />}
              onPress={async () => {
                const f = await pickFileWeb('.pdf,.jpg,.jpeg,.png')
                setScans(f ? [f] : [])
              }}
            >
              {scans[0]
                ? `📎 ${scans[0].name}`
                : t({ ru: 'Или прикрепить скан подписанного договора (PDF/JPG/PNG)', en: 'Or attach signed contract scan (PDF/JPG/PNG)' })}
            </Button>
          ) : Platform.OS === 'web' && !contractFinal ? (
            <YStack gap={6}>
              {scans.map((f, i) => (
                <XStack key={`${i}-${f.name}`} alignItems="center" gap={8} paddingLeft={10} borderWidth={1} borderColor="$borderSecondary" borderRadius={7}>
                  <Icon name="paperclip" size={14} color="$muted" />
                  <Text flex={1} fontSize={13} color="$color" numberOfLines={1}>{f.name}</Text>
                  <IconButton aria-label={`${t({ ru: 'Убрать', en: 'Remove', az: 'Sil' })} ${f.name}`} onPress={() => setScans(scans.filter((_, j) => j !== i))}>
                    <Icon name="x" size={14} color="$muted" />
                  </IconButton>
                </XStack>
              ))}
              <Button
                variant="default"
                block
                icon={<Icon name="paperclip" size={15} color="$muted" />}
                onPress={async () => {
                  const picked = await pickFilesWeb('.pdf,.jpg,.jpeg,.png')
                  if (picked.length) setScans((prev) => [...prev, ...picked])
                }}
              >
                {scans.length
                  ? t({ ru: 'Добавить ещё скан', en: 'Add another scan', az: 'Daha bir skan əlavə et' })
                  : t({ ru: 'Прикрепить сканы приказа (PDF/JPG/PNG)', en: 'Attach order scans (PDF/JPG/PNG)', az: 'Əmrin skanlarını əlavə et (PDF/JPG/PNG)' })}
              </Button>
            </YStack>
          ) : Platform.OS !== 'web' ? (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Прикрепление скана доступно в веб-версии портала', en: 'Scan upload is available in the web version' })}
            </Text>
          ) : null}
        </YStack>
      )}

      {/* Срок действия договора (решение бизнеса 27.08.2026). Стоит рядом со сканом намеренно:
          и то и другое появляется в один момент — когда экземпляр подписан. Обе даты уходят в 1С
          как `srok_deystviya_s`/`_po`, там они обязательны. Нижняя граница явная: дефолт DateField
          (1-е число месяца) — правило кадровых заявок, а договор подписывают раньше, чем автор
          доходит до этого шага, и «Действует с» — это ещё и дата договора в 1С (13.10.2026). */}
      {contractFinal && (
        <YStack gap={6}>
          <Text fontSize={12} color="$muted">
            {t({ ru: 'Укажите срок действия договора — он уходит в 1С вместе со сканом. Если договор не подписан, срок не нужен.',
                 en: 'Set the contract validity period — it goes to 1C together with the scan. Not needed if the contract is not signed.',
                 az: 'Müqavilənin qüvvədə olma müddətini göstərin — o, skanla birlikdə 1C-yə gedir. Müqavilə imzalanmayıbsa, müddət lazım deyil.' })}
          </Text>
          <XStack gap={10}>
            <YStack flex={1} gap={4}>
              <Text fontSize={12} color="$muted">{t({ ru: 'Действует с', en: 'Valid from' })}</Text>
              <DateField value={dates.dateFrom} onChange={setDate('dateFrom')} min={CONTRACT_DATE_MIN} placeholder="ГГГГ-ММ-ДД" />
            </YStack>
            <YStack flex={1} gap={4}>
              <Text fontSize={12} color="$muted">{t({ ru: 'Действует по', en: 'Valid until' })}</Text>
              <DateField value={dates.dateTo} onChange={setDate('dateTo')} min={dates.dateFrom || CONTRACT_DATE_MIN} placeholder="ГГГГ-ММ-ДД" />
            </YStack>
          </XStack>
          {!datesOrdered && (
            <Text fontSize={12} color={tone('red').text}>
              {t({ ru: 'Дата окончания раньше даты начала', en: 'End date is before the start date' })}
            </Text>
          )}
        </YStack>
      )}

      {/* Замещающий: HR правит его на своём шаге, пока согласует отпуск (в листе «Отклонить» поля нет). */}
      {hrDeputy && decisions.includes('approve') && (
        <PersonPickField
          label={{
            ru: 'Замещающий на время отпуска',
            en: 'Substitute during vacation',
            az: 'Məzuniyyət müddətində əvəz edən',
          }}
          value={deputy}
          onChange={setPickedDeputy}
          excludeIds={detail?.initiator?.id ? [detail.initiator.id] : []}
        />
      )}

      {/* Approver decisions (live task) */}
      {decisions.length > 0 && (
        <XStack gap={10} flexWrap="wrap">
          {decisions.map((d) => {
            const primary = d === 'approve' || d === 'sign' || d === 'resubmit'
            const blocked =
              detailPending ||
              uploading ||
              ((needComment || needsReason(d)) && !comment.trim()) ||
              (needScan && d === 'approve' && !scans.length && !signed) ||
              (contractFinal && d === 'approve' && (!datesFilled || !datesOrdered))
            const label = decisionLabel(d, stage, detail?.type)
            return (
              <Button
                key={d}
                flex={1}
                minWidth={120}
                variant={primary ? 'primary' : 'default'}
                disabled={decide.isPending || blocked}
                opacity={decide.isPending || blocked ? 0.5 : 1}
                onPress={() => {
                  if (blocked) return
                  void doDecide(d)
                }}
              >
                {t(label)}
              </Button>
            )
          })}
        </XStack>
      )}
      {decide.error instanceof Error && (
        <Text fontSize={12} color={tone('red').text}>{t(errorText(decide.error))}</Text>
      )}
      {uploadErr ? <Text fontSize={12} color={tone('red').text}>{uploadErr}</Text> : null}
    </>
  )
}
